"use client";

import React, { useState, useEffect, lazy, Suspense } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebaseConfig';
import RelayList from './RelayList';
import PhoneRelayList from './PhoneRelayList';
import DisableRelayDialog from './DisableRelayDialog';
import PhoneVerificationModal from './PhoneVerificationModal';
import EmailPhoneToggle from './EmailPhoneToggle';
import BuyCustomDomain from './buyCustomdomain';
import { Cog6ToothIcon, ShieldCheckIcon, XMarkIcon, ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { Bars3Icon } from '@heroicons/react/20/solid';
import { GiDominoMask, GiRadarSweep, GiSoundWaves, GiEchoRipples, GiSpeedometer } from "react-icons/gi";
import { MdContacts } from "react-icons/md";
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';
import { collection, getDocs } from 'firebase/firestore';
import { useLocation, useSearchParams } from 'react-router-dom';
import usePageTitle from './utils/usePageTitle';
import { AtSymbolIcon, PhoneIcon, CheckIcon } from '@heroicons/react/24/outline';

const RelayDetails = lazy(() => import('./RelayDetails'));
const PhoneRelayDetails = lazy(() => import('./PhoneRelayDetails'));

// Function to filter navigation items based on current path
function getFilteredNavigation(pathname) {
  return [
    { name: 'Home', href: '/overview', icon: GiSpeedometer, current: pathname === '/overview' },
    { name: 'Relay', href: '/dashboard', icon: GiDominoMask, current: pathname === '/dashboard' },
    { name: 'Messages', href: '/dashboard/trace', icon: GiEchoRipples, current: pathname === '/dashboard/trace' },
    { name: 'World Call', href: '/world-call', icon: GiSoundWaves, current: pathname === '/world-call' },
    { name: 'Contacts', href: '/contacts', icon: MdContacts, current: pathname === '/contacts' },
    { name: 'Monitor', href: '/dashboard/monitor', icon: GiRadarSweep, current: pathname === '/dashboard/monitor' },
  ];
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// ActivityFeed Component
function ActivityFeed({ userDoc, relays, mode }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userDoc?.uid) return;
    
    const loadRealActivities = async () => {
      setLoading(true);
      const uid = userDoc.uid;

      try {
        // Fetch real user actions from the action log
        const response = await fetch('/api/getUserActions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uid: uid,
            category: mode === 'email' ? 'alias' : 'phone', // Filter by current mode
            limit: 15
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setActivities(data.actions || []);
          } else {
            console.error('Failed to fetch actions:', data.error);
            setActivities([]);
          }
        } else {
          console.error('API request failed:', response.status);
          setActivities([]);
        }
      } catch (error) {
        console.error('Error loading real activities:', error);
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    loadRealActivities();
  }, [userDoc, mode]); // Remove relays dependency since we're getting data from action log

  const getActivityIcon = (actionType) => {
    switch (actionType) {
      case 'alias_created':
        return <AtSymbolIcon className="h-4 w-4" />;
      case 'alias_activated':
        return <CheckIcon className="h-4 w-4" />;
      case 'alias_deactivated':
        return <XMarkIcon className="h-4 w-4" />;
      case 'phone_created':
        return <PhoneIcon className="h-4 w-4" />;
      case 'phone_activated':
        return <CheckIcon className="h-4 w-4" />;
      case 'phone_deactivated':
        return <XMarkIcon className="h-4 w-4" />;
      default:
        return <GiEchoRipples className="h-4 w-4" />;
    }
  };

  const getActivityColor = (actionType) => {
    switch (actionType) {
      case 'alias_created':
      case 'phone_created':
        return 'text-blue-400 bg-blue-500/20';
      case 'alias_activated':
      case 'phone_activated':
        return 'text-green-400 bg-green-500/20';
      case 'alias_deactivated':
      case 'phone_deactivated':
        return 'text-red-400 bg-red-500/20';
      case 'alias_label_updated':
      case 'phone_label_updated':
        return 'text-orange-400 bg-orange-500/20';
      default:
        return 'text-gray-400 bg-gray-500/20';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    const activityTime = new Date(timestamp);
    const now = new Date();
    const diff = now - activityTime;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-white/10 rounded-lg"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-white/10 rounded w-3/4"></div>
                <div className="h-3 bg-white/10 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8">
        <GiEchoRipples className="h-12 w-12 mx-auto mb-4 text-white/30" />
        <p className="text-white/60 text-sm">No recent activity</p>
        <p className="text-white/40 text-xs mt-1">
          {mode === 'email' ? 'Create and manage email aliases' : 'Create and manage phone numbers'} to see activity here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-h-[600px] overflow-y-auto">
      {activities.map((activity) => (
        <div key={activity.id} className="flex items-start space-x-3 group">
          <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${getActivityColor(activity.actionType)}`}>
            {getActivityIcon(activity.actionType)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-white truncate">
                {activity.description}
              </p>
              <span className="text-xs text-white/40 ml-2 whitespace-nowrap">
                {formatTimestamp(activity.timestamp)}
              </span>
            </div>
            {/* Show additional metadata */}
            {activity.metadata?.aliasEmail && (
              <p className="text-xs text-white/60 mt-1 truncate">
                {activity.metadata.aliasEmail}
              </p>
            )}
            {activity.metadata?.aliasNumber && (
              <p className="text-xs text-white/60 mt-1 truncate">
                {activity.metadata.aliasNumber}
              </p>
            )}
            {activity.metadata?.oldStatus && activity.metadata?.newStatus && (
              <p className="text-xs text-white/40 mt-1">
                {activity.metadata.oldStatus} → {activity.metadata.newStatus}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  usePageTitle('Dashboard');

  const [relays, setRelays] = useState([]);
  const [selectedRelay, setSelectedRelay] = useState(null);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("phone"); // "email" or "phone"
  const [userDoc, setUserDoc] = useState(null);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [showCreateRelay, setShowCreateRelay] = useState(false); // For showing BuyCustomDomain UI
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigation = getFilteredNavigation(location.pathname);
  const settingsNavItem = { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, current: location.pathname === '/settings' };

  // Check URL parameters for mode on component mount
  useEffect(() => {
    const modeParam = searchParams.get('mode');
    if (modeParam === 'email' || modeParam === 'phone') {
      setMode(modeParam);
    }
  }, [searchParams]);

  /* ───────────────────────────── helpers ───────────────────────────── */
  function getCookie(name) {
    const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[2]) : null;
  }

  /* Function to fetch user aliases (adds attribution in POST body) */
  const fetchUserAliases = async (user) => {
    try {
      // ▲ NEW: read UTM/referrer cookies once per request
      /* richer attribution: replace the old 4-field object with this*/
      const attribution = {
        /* classic UTM */
        utm_source:   getCookie('utm_source'),
        utm_medium:   getCookie('utm_medium'),
        utm_campaign: getCookie('utm_campaign'),
        utm_term:     getCookie('utm_term'),
        utm_content:  getCookie('utm_content'),

        /* ad-platform click IDs */
        gclid:   getCookie('gclid'),
        fbclid:  getCookie('fbclid'),
        msclkid: getCookie('msclkid'),

        /* traffic + landing context */
        referrer: getCookie('referrer'),
        landing:  getCookie('landing'),

        /* device / locale */
        user_agent:  getCookie('user_agent'),
        lang:        getCookie('lang'),
        tz_offset:   getCookie('tz_offset'),
        tz_name:     getCookie('tz_name'),

        /* screen & hardware */
        scr_w:       getCookie('scr_w'),
        scr_h:       getCookie('scr_h'),
        scr_cd:      getCookie('scr_cd'),
        mem_gb:      getCookie('mem_gb'),
        cpu_threads: getCookie('cpu_threads'),

        /* network & session */
        ip:  getCookie('ip'),
        sid: getCookie('sid'),
      };

      const response = await fetch('/api/createAliases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: user.email || '', // Handle phone-only users who don't have email
          uid: user.uid, 
          attribution 
        }),
      });

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        console.log('Aliases received:', data.aliases);
        
        // Fetch phone aliases from the subcollection using the db instance
        const phoneAliasesSnapshot = await getDocs(collection(db, 'userAliases', user.uid, 'phoneAliases'));
        const phoneAliases = [];
        phoneAliasesSnapshot.forEach(doc => {
          phoneAliases.push({
            ...doc.data(),
            aliasNumber: doc.id // Use the document ID as the aliasNumber
          });
        });
        
        setRelays(data.aliases);
        setUserDoc({ 
          ...data, 
          uid: user.uid,
          phoneAliases // Add phone aliases to the userDoc
        });
        
        // Remove phone verification check - users can now access phone modules without verification
        setShowPhoneVerification(false);
      } else {
        console.error('Unexpected response format:', await response.text());
      }
    } catch (error) {
      console.error('Error fetching aliases:', error);
    }
  };

  // Monitor authentication state and fetch user aliases.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setLoading(true);
        await fetchUserAliases(user);
        setLoading(false);
      } else {
        setRelays([]);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [mode]);

  // Add TriplePixel purchase confirmation event if user came from checkout page
  useEffect(() => {
    // Check if user came from checkout page using referrer or sessionStorage
    const referrer = document.referrer;
    const cameFromCheckout = referrer.includes('/checkout') || sessionStorage.getItem('cameFromCheckout');
    
    // Only fire the event if the user came from checkout and not just reloading the dashboard
    if (cameFromCheckout && !sessionStorage.getItem('purchaseEventFired')) {
      // Load the first.js script
      const script = document.createElement('script');
      script.src = 'https://api.config-security.com/first.js?host=www.chatodyssey.com&plat=stripe';
      document.head.appendChild(script);
      
      // Mark that we've fired the purchase event to avoid duplicate events
      sessionStorage.setItem('purchaseEventFired', 'true');
      // Clear the checkout flag
      sessionStorage.removeItem('cameFromCheckout');
    }
  }, []);

  // Toggle between email and phone modes.
  const handleToggle = () => {
    const newMode = mode === "email" ? "phone" : "email";
    setMode(newMode);
    // Remove phone verification check - users can access phone mode without verification
    setShowPhoneVerification(false);
    // Reset selected relay when toggling modes.
    setSelectedRelay(null);
  };

  // Updated: handleRelaySelect receives only the relay object.
  const handleRelaySelect = (relay) => {
    console.log("Parent received relay:", relay);
    setSelectedRelay(relay);
    setShowCreateRelay(false);
  };

  // Add a new function to handle relay updates
  const handleRelayUpdate = (updatedRelay) => {
    console.log("Handling relay update:", updatedRelay);
    // Update the selected relay
    setSelectedRelay(updatedRelay);
    
    // For phone relays, update the userDoc state
    if (mode === "phone" && userDoc) {
      const updatedPhoneAliases = userDoc.phoneAliases.map(alias => 
        alias.aliasNumber === updatedRelay.aliasNumber ? updatedRelay : alias
      );
      setUserDoc({ ...userDoc, phoneAliases: updatedPhoneAliases });
    }
    
    // For email relays, update the relays state
    if (mode === "email") {
      const updatedRelays = relays.map(relay => 
        relay.address === updatedRelay.address ? updatedRelay : relay
      );
      setRelays(updatedRelays);
    }
  };

  // When the user clicks "Get a Custom Relay" in RelayList/PhoneRelayList.
  const handleOpenBuyCustomDomain = () => {
    setShowCreateRelay(true);
    setSelectedRelay(null);
  };

  // Handle "Use Custom Relay" action.
  const handleUseCustomRelay = () => {
    console.log("Use Custom Relay clicked");
    // Add your custom relay logic here.
  };

  // Handle "Use Standard Relay" action.
  const handleUseStandardRelay = () => {
    console.log("Use Standard Relay clicked");
    // Add your standard relay logic here.
  };

  // Callback when the phone is verified.
  const handlePhoneVerified = async (verifiedPhone) => {
    console.log("Phone verified:", verifiedPhone);
    // Update the local state with the verified phone.
    const updatedDoc = { ...userDoc, forwardToNumber: verifiedPhone };
    setUserDoc(updatedDoc);
    setShowPhoneVerification(false);
    // Optionally, re-fetch the user aliases to update PhoneRelayList.
    if (auth.currentUser) {
      await fetchUserAliases(auth.currentUser);
    }
  };

  // When the user closes the verification modal.
  const handleVerificationClose = () => {
    // No longer switch to email mode - users can stay in phone mode
    setShowPhoneVerification(false);
  };

  return (
    <>
      <div className="flex min-h-screen text-white" 
           style={{background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'}}>
        {/* Mobile sidebar - Liquid Glass */}
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop transition className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0" />
          <div className="fixed inset-0 flex">
            <DialogPanel transition className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full">
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                  </button>
                </div>
              </TransitionChild>
              
              <div 
                className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl"
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
                }}
              >
                <div className="flex h-16 shrink-0 items-center justify-center px-4">
                  <img
                    alt="Relay"
                    src="/images/logowhite.png"
                    className="h-8 w-auto"
                  />
                    </div>
                <nav className="flex flex-1 flex-col px-4">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                      <ul role="list" className="space-y-3">
                          {navigation.map((item) => (
                            <li key={item.name}>
                              <a
                                href={item.href}
                              className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200"
                              onClick={() => setSidebarOpen(false)}
                            >
                              <item.icon 
                                aria-hidden="true" 
                                className={classNames(
                                  "h-6 w-6 shrink-0 transition-colors duration-200",
                                  item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                                )}
                              />
                              <span className={classNames(
                                "transition-colors duration-200",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )}>
                                {item.name}
                              </span>
                              </a>
                            </li>
                          ))}
                        </ul>
                      </li>
                    <li className="mt-auto mb-4">
                    <a
                      href={settingsNavItem.href}
                        className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <settingsNavItem.icon 
                          aria-hidden="true" 
                      className={classNames(
                            "h-6 w-6 shrink-0 transition-colors duration-200",
                            settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                          )} 
                        />
                        <span className={classNames(
                          "transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )}>
                          {settingsNavItem.name}
                        </span>
                    </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Desktop sidebar - Liquid Glass Collapsible */}
        <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ${
          sidebarExpanded ? 'lg:w-72' : 'lg:w-20'
        }`}>
          <div className="relative">
            <div 
              className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl relative"
              style={{
                background: 'rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)',
                height: 'calc(100vh - 2rem)'
              }}
            >
              {/* Header with logo */}
              <div className={`flex h-16 shrink-0 items-center justify-center transition-all duration-300 ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
                <img
                  alt="Relay"
                  src="/images/logowhite.png"
                  className={`transition-all duration-300 ${sidebarExpanded ? 'h-8 w-auto' : 'h-10 w-auto max-w-none'}`}
                  style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.1))' }}
                />
              </div>

              <nav className={`flex flex-1 flex-col ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="space-y-3">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <a
                      href={item.href}
                      className={classNames(
                              'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg',
                              sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                            )}
                            title={!sidebarExpanded ? item.name : undefined}
                          >
                            <item.icon 
                              aria-hidden="true" 
                              className={classNames(
                                "h-6 w-6 shrink-0 transition-colors duration-200",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )} 
                            />
                            {sidebarExpanded && (
                              <span className={classNames(
                                "transition-opacity duration-300",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )}>
                                {item.name}
                              </span>
                            )}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </li>
                  <li className="mt-auto mb-4">
                    <a
                      href={settingsNavItem.href}
                      className={classNames(
                        'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg',
                        sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                      )}
                      title={!sidebarExpanded ? settingsNavItem.name : undefined}
                    >
                      <settingsNavItem.icon 
                        aria-hidden="true" 
                        className={classNames(
                          "h-6 w-6 shrink-0 transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )} 
                      />
                      {sidebarExpanded && (
                        <span className={classNames(
                          "transition-opacity duration-300",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )}>
                          {settingsNavItem.name}
                        </span>
                      )}
                    </a>
                  </li>
              </ul>
              </nav>
            </div>

            {/* Right border expand/collapse area */}
            <div 
              className="absolute top-0 right-0 w-4 h-full cursor-pointer group flex items-center justify-center"
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              style={{ zIndex: 60 }}
            >
              {/* Hover indicator */}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/10 backdrop-blur-md rounded-full p-1 border border-white/20">
                {sidebarExpanded ? (
                  <ChevronLeftIcon className="h-4 w-4 text-white/70" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-white/70" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className={`flex-1 transition-all duration-300 ${
          sidebarExpanded ? 'lg:pl-80' : 'lg:pl-28'
        }`}>
          <div className="p-4 lg:p-6">
            {/* Mobile Menu Button and Top Right Toggle */}
            <div className="flex items-center justify-between mb-6">
              <button type="button" onClick={() => setSidebarOpen(true)} className="lg:hidden bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-3 text-white hover:bg-black/30 transition-all"
                     style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon aria-hidden="true" className="h-6 w-6" />
              </button>
              <EmailPhoneToggle mode={mode} onToggle={handleToggle} />
            </div>

            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-light text-white mb-2">
                {mode === "phone" ? "Phone Relays" : "Email Relays"}
              </h1>
              <p className="text-white/60">
                {mode === "phone" 
                  ? "Manage your virtual phone numbers for calls and texts" 
                  : "Manage your email aliases and forwarding"
                }
              </p>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
              {/* Left Panel: Relay List */}
              <div className="xl:col-span-4">
                <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6 h-full"
                     style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
                  <h2 className="text-xl font-light text-white mb-4">
                    {mode === "phone" ? "Phone Numbers" : "Email Aliases"}
                  </h2>
                  <div className="max-h-[600px] overflow-hidden">
                {mode === "phone" ? (
                  <PhoneRelayList
                    userDoc={userDoc}
                    onSelect={handleRelaySelect}
                    onCreate={handleOpenBuyCustomDomain}
                    onRefresh={() => {
                      if (auth.currentUser) {
                        fetchUserAliases(auth.currentUser);
                      }
                    }}
                    autoOpenModal={searchParams.get('action') === 'create'}
                  />
                ) : (
                  <RelayList
                    relays={relays}
                    onSelect={handleRelaySelect}
                        onCreate={handleUseStandardRelay}
                        onOpenBuyCustomDomain={handleOpenBuyCustomDomain}
                        onUseCustomRelay={handleUseCustomRelay}
                  />
                )}
                  </div>
                </div>
              </div>

              {/* Middle Panel: Relay Details */}
              <div className="xl:col-span-5">
                <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6 h-full"
                     style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
                {showCreateRelay ? (
                  <BuyCustomDomain 
                    userId={userDoc?.uid}
                    userEmail={userDoc?.email}
                  />
                ) : (
                  loading ? (
                    <div className="flex items-center justify-center h-64 w-full">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
                        <p className="mt-4 text-gray-400">Loading data...</p>
                      </div>
                    </div>
                  ) : selectedRelay ? (
                    <Suspense fallback={
                      <div className="flex items-center justify-center h-64 w-full">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
                          <p className="mt-4 text-gray-400">Loading relay details...</p>
                        </div>
                      </div>
                    }>
                      {mode === "phone" ? (
                        <PhoneRelayDetails
                          phoneRelay={selectedRelay}
                          onClose={() => setSelectedRelay(null)}
                          onRelayUpdate={handleRelayUpdate}
                        />
                      ) : (
                        <RelayDetails 
                          relay={selectedRelay} 
                          onRelayUpdate={handleRelayUpdate}
                        />
                      )}
                    </Suspense>
                  ) : (
                      <div className="flex items-center justify-center h-64 text-white/60">
                        <div className="text-center">
                          <GiDominoMask className="h-16 w-16 mx-auto mb-4 text-white/30" />
                          <h3 className="text-lg font-medium mb-2">Select a Relay</h3>
                          <p className="text-sm">Choose a relay from the list to view and manage its details</p>
                        </div>
                      </div>
                  )
                )}
                </div>
              </div>

              {/* Right Panel: Activity Feed */}
              <div className="xl:col-span-3">
                <div className="bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-6 h-full"
                     style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
                  <div className="flex items-center gap-x-3 mb-6">
                    <div className="flex items-center justify-center w-8 h-8 bg-purple-500/20 rounded-lg">
                      <GiEchoRipples className="h-5 w-5 text-purple-400" />
                    </div>
                    <h3 className="text-xl font-light text-white">Recent Activity</h3>
                  </div>
                  
                  <ActivityFeed 
                    userDoc={userDoc} 
                    relays={relays} 
                    mode={mode}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDisableDialog && (
        <DisableRelayDialog
          open={showDisableDialog}
          setOpen={setShowDisableDialog}
          onDeactivate={() => {
            alert('Relay deactivated');
            setShowDisableDialog(false);
          }}
        />
      )}

      {/* Phone Verification Modal */}
      {showPhoneVerification && (
        <PhoneVerificationModal 
          uid={userDoc?.uid} 
          onVerified={handlePhoneVerified} 
          onClose={handleVerificationClose} 
        />
      )}
    </>
  );
}